<script setup>
import { computed, ref } from 'vue';
import SidebarGroupLeaf from './SidebarGroupLeaf.vue';
import SidebarGroupSeparator from './SidebarGroupSeparator.vue';

import { useSidebarContext } from './provider';
import { useEventListener } from '@vueuse/core';

const props = defineProps({
  isExpanded: { type: Boolean, default: false },
  label: { type: String, required: true },
  icon: { type: [Object, String], default: null },
  children: { type: Array, default: undefined },
  activeChild: { type: Object, default: undefined },
  startClosed: { type: Boolean, default: false },
  plain: { type: Boolean, default: false },
});

const { isAllowed } = useSidebarContext();
const scrollableContainer = ref(null);
const opened = ref(false);

const showChildren = computed(
  () => props.isExpanded && (!props.startClosed || opened.value)
);

const accessibleItems = computed(() =>
  props.children.filter(child => {
    return child.to && isAllowed(child.to);
  })
);

const hasAccessibleItems = computed(() => {
  return accessibleItems.value.length > 0;
});

const isScrollable = computed(() => {
  return accessibleItems.value.length > 7;
});

const scrollEnd = ref(false);

// set scrollEnd to true when the scroll reaches the end
useEventListener(scrollableContainer, 'scroll', () => {
  const { scrollHeight, scrollTop, clientHeight } = scrollableContainer.value;
  scrollEnd.value = scrollHeight - scrollTop === clientHeight;
});
</script>

<template>
  <li
    v-if="plain && hasAccessibleItems && startClosed"
    v-show="isExpanded"
    class="py-0.5 ltr:pl-2 rtl:pr-2 rtl:mr-3 ltr:ml-3 relative text-n-slate-11 child-item before:bg-n-slate-4 after:bg-transparent after:border-n-slate-4 before:left-0 rtl:before:right-0 min-w-0"
  >
    <button
      type="button"
      class="flex h-8 w-full items-center gap-2 px-2 py-1 rounded-lg min-w-0 hover:bg-gradient-to-r from-transparent via-n-slate-3/70 to-n-slate-3/70"
      @click="opened = !opened"
    >
      <div class="flex-1 truncate min-w-0 text-sm text-start">{{ label }}</div>
      <span
        class="i-lucide-chevron-down size-3.5 shrink-0 text-n-slate-10 transition-transform"
        :class="{ 'rotate-180': opened }"
      />
    </button>
  </li>
  <button
    v-else-if="hasAccessibleItems && startClosed"
    v-show="isExpanded"
    type="button"
    class="flex w-full items-center rounded-lg text-start text-n-slate-10 hover:bg-n-alpha-1"
    @click="opened = !opened"
  >
    <SidebarGroupSeparator :label :icon class="my-1 min-w-0 flex-1" />
    <span
      class="i-lucide-chevron-down me-2 size-4 flex-shrink-0 transition-transform"
      :class="{ 'rotate-180': opened }"
    />
  </button>
  <SidebarGroupSeparator
    v-else-if="hasAccessibleItems"
    v-show="isExpanded"
    :label
    :icon
    class="my-1"
  />
  <ul
    v-if="children.length && showChildren"
    class="m-0 list-none reset-base relative group min-w-0"
  >
    <!-- Each element has h-8, which is 32px, we will show 7 items with one hidden at the end,
    which is 14rem. Then we add 16px so that we have some text visible from the next item  -->
    <div
      ref="scrollableContainer"
      class="min-w-0"
      :class="{
        'max-h-[calc(14rem+16px)] overflow-y-scroll no-scrollbar': isScrollable,
      }"
    >
      <SidebarGroupLeaf
        v-for="child in children"
        v-show="showChildren"
        v-bind="child"
        :key="child.name"
        :active="activeChild?.name === child.name"
      />
    </div>
    <div
      v-if="isScrollable && showChildren"
      v-show="!scrollEnd"
      class="absolute bg-gradient-to-t from-n-background w-full h-12 to-transparent -bottom-1 pointer-events-none flex items-end justify-end px-2 animate-fade-in-up"
    >
      <svg
        width="16"
        height="24"
        viewBox="0 0 16 24"
        fill="none"
        class="text-n-slate-9 opacity-50 group-hover:opacity-100"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M4 4L8 8L12 4"
          stroke="currentColor"
          opacity="0.5"
          stroke-width="1.33333"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <path
          d="M4 10L8 14L12 10"
          stroke="currentColor"
          opacity="0.75"
          stroke-width="1.33333"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <path
          d="M4 16L8 20L12 16"
          stroke="currentColor"
          stroke-width="1.33333"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </div>
  </ul>
</template>
